<template>
  <main class="m-top">
    <h2>{{ $t('pages.stripePayment.payment') }}</h2>
    <h3 v-if="showButtons">{{ $t('pages.stripePayment.selectPayment') }}</h3>
    <div class="message message__error" v-for="message in errorMessages" :key="`err-${message}`">
      <h4 class="message__title message__title--error">
        {{ $t('pages.stripePayment.errorTitle') }}
      </h4>
      <p class="message__text">{{ $t(message) }}</p>
    </div>
    <div class="message message__info" v-for="message in infoMessages" :key="`info-${message}`">
      <h4 class="message__title message__title--info">{{ $t('pages.stripePayment.infoTitle') }}</h4>
      <div class="loading">
        <p class="message__text">{{ $t(message) }}</p>
        <div id="spinner"></div>
      </div>
    </div>
    <form id="payment-form" @submit.prevent="handleSubmit">
      <div id="payment-element"></div>
      <div class="campground__navigation">
        <CiButton
          class="navigation__button button--secondary"
          :disabled="(status && status.loading) || processingPayment"
          v-if="activeStep > 1"
          @click="prevStep"
        >
          <iconArrowBlack class="icon icon--animated"></iconArrowBlack>
          <span>{{ $t('generic.buttons.stepBack') }}</span>
        </CiButton>
        <CiButton
          @click="handleSubmit"
          :disabled="isLoading"
          class="button__pay-now button--primary"
          v-if="showButtons"
        >
          <template v-if="!isLoading">
            <span> {{ $t('pages.stripePayment.payNow') }}</span>
          </template>
          <template v-if="isLoading">
            <span>{{ $t('generic.buttons.loading') }}</span>
            <span class="spinner"></span>
          </template>
        </CiButton>
      </div>
    </form>
  </main>
</template>

<script>
import { Api } from '@/api/api'
import CiButton from '@/components/CiButton/CiButton.vue'
import { mapActions, mapGetters } from 'vuex'
import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'

export default {
  components: { iconArrowWhite, iconArrowBlack, CiButton },
  data() {
    return {
      isLoading: false,
      stripe: null,
      elements: null,
      errorMessages: [],
      infoMessages: [],
      showButtons: true,
      // Speichert, wie die Billing-Felder im Payment Element gesetzt wurden
      billingFieldsMode: {
        name: 'auto',
        email: 'auto',
        phone: 'auto',
        country: 'auto',
      },
    }
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      checkout: 'checkout',
      price: 'price',
      status: 'status',
      bookingConfirmation: 'confirmation',
      selectedCampsite: 'selectedCampsite',
      processingPayment: 'processingPayment',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      currencies: 'currencies',
      sessionTimeout: 'sessionTimeout',
      urls: 'urls',
      paymentInfos: 'paymentInfos',
    }),
  },
  async mounted() {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentStatus = urlParams.get('redirect_status')
    const paymentIntent = urlParams.get('payment_intent')

    if (paymentStatus) {
      if (paymentStatus === 'failed') {
        this.addErrorMessage('pages.stripePayment.paymentFailed')
        await this.createPaymentIntent()
      } else if (paymentStatus === 'succeeded') {
        this.errorMessages = []
        this.addInfoMessage('pages.stripePayment.paymentProcessing')
        this.showButtons = false
        const isUpdated = await this.checkStatus(paymentIntent)
        if (isUpdated === 'confirmed') {
          this.$router.push({
            name: 'Confirmation',
            params: {
              language: this.$route.params.language,
              campground: this.$route.params.campground,
              bookingId: this.$route.params.bookingId,
            },
          })
        } else if (isUpdated === 'failed') {
          this.infoMessages = []

          this.addErrorMessage('pages.stripePayment.paymentFailed')
        } else if (isUpdated === 'pending') {
          console.log('Webhook has still not arrived!')
          this.$router.push({
            name: 'Confirmation',
            params: {
              language: this.$route.params.language,
              campground: this.$route.params.campground,
              bookingId: this.$route.params.bookingId,
            },
          })
        }
      }
    } else {
      await this.createPaymentIntent()
    }
  },

  methods: {
    // Waits until Stripe.js (loaded via CDN) is available on window
    waitForStripe() {
      return new Promise((resolve, reject) => {
        if (window && window.Stripe) {
          return resolve(window.Stripe)
        }
        let attempts = 0
        const maxAttempts = 50 // ~5s at 100ms
        const interval = setInterval(() => {
          attempts++
          if (window && window.Stripe) {
            clearInterval(interval)
            resolve(window.Stripe)
          } else if (attempts >= maxAttempts) {
            clearInterval(interval)
            reject(new Error('Stripe.js konnte nicht geladen werden'))
          }
        }, 100)
      })
    },

    ...mapActions('booking', {
      setProcessingPayment: 'setProcessingPayment',
    }),

    processPayment() {
      this.setProcessingPayment(true)
    },

    finishProcessingPayment() {
      this.setProcessingPayment(false)
    },

    addErrorMessage(message) {
      this.errorMessages = []
      this.errorMessages.push(message)
    },

    addInfoMessage(message) {
      this.infoMessages = []
      this.infoMessages.push(message)
    },
    prevStep() {
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: 5 },
      })
    },

    async createPaymentIntent() {
      const bookingId = this.$route.params.bookingId
      const provider_info =
        this.selectedCampsite.campsite_type === 'accommodation'
          ? this.paymentInfos.rentals
          : this.paymentInfos.pitches

      const stripe_key = provider_info.payment_provider_settings.data.public_key

      if (!stripe_key) {
        console.error('Missing Stripe key:', stripe_key)
        this.addErrorMessage('pages.stripePayment.genericError')
        return
      }

      try {
        this.isLoading = true

        // Step 1: Ensure Stripe.js (CDN) is loaded
        await this.waitForStripe()

        // Step 2: Calculate amount due (full or partial payment)
        let amountDue = this.price.total
        if (this.price.deposit_percent && this.price.deposit_value < this.price.total) {
          amountDue = this.price.deposit_value
        }

        // Step 3: Call backend to create PaymentIntent
        const {
          clientSecret,
          stripeAccountId,
          error: backendError,
        } = await Api.createStripePaymentIntent(amountDue, bookingId)

        if (backendError) {
          console.error(backendError.message)
          this.addErrorMessage('pages.stripePayment.genericError')
          return
        }

        // Step 4: Initialize Stripe client with stripeAccount
        this.stripe = window.Stripe(stripe_key, {
          locale: `${this.language.code}`,
          stripeAccount: stripeAccountId,
        })

        if (!this.stripe) {
          console.error('Stripe.js failed to load.')
          this.addErrorMessage('pages.stripePayment.genericError')
          return
        }

        // Step 5: Initialize Stripe Elements with clientSecret
        this.elements = this.stripe.elements({ clientSecret })

        // Step 6: Configure billing fields based on existing data
        const b = this.billing || {}
        const hasEmail = !!b.email
        const hasPhone = !!b.phone
        const hasCountry = !!(b.country && b.country.code)

        this.billingFieldsMode = {
          name: 'auto',
          email: hasEmail ? 'never' : 'auto',
          phone: hasPhone ? 'never' : 'auto',
          country: hasCountry ? 'never' : 'auto',
        }

        // Step 7: Create and mount payment element
        const paymentElement = this.elements.create('payment', {
          layout: 'accordion',
          paymentMethodOrder: ['card'],
          fields: {
            billingDetails: {
              address: {
                country: this.billingFieldsMode.country,
              },
              name: this.billingFieldsMode.name,
              email: this.billingFieldsMode.email,
              phone: this.billingFieldsMode.phone,
            },
          },
        })

        if (process.env.NODE_ENV !== 'production') {
          console.log('[Stripe] paymentElement created', paymentElement)
        }

        paymentElement.mount('#payment-element')
      } catch (error) {
        console.error('Unexpected error:', error)
        this.addErrorMessage('pages.stripePayment.genericError')
      } finally {
        this.isLoading = false
      }
    },

    async checkStatus(paymentIntent) {
      let attempts = 0
      const maxAttempts = 10
      const interval = 1000

      const bookingId = this.$route.params.bookingId

      return new Promise(resolve => {
        const intervalId = setInterval(async () => {
          try {
            const response = await Api.getStripePaymentStatus(bookingId, paymentIntent)
            if (response.status === 'SUCCESS') {
              console.log(`resolved after ${attempts} attempts`)
              clearInterval(intervalId)
              resolve('confirmed')
              this.showButtons = false
            } else if (response.status === 'FAILED') {
              console.log(`resolved after ${attempts} attempts`)
              clearInterval(intervalId)
              resolve('failed')
              await this.createPaymentIntent()
              this.showButtons = true
            }
          } catch (error) {
            console.error('Error fetching payment status', error)
          }
          attempts++
          if (attempts >= maxAttempts) {
            clearInterval(intervalId)
            resolve('pending')
            this.showButtons = false
          }
        }, interval)
      })
    },

    async handleSubmit() {
      if (this.isLoading) {
        return
      }

      this.isLoading = true
      this.processPayment()

      try {
        const _billing = this.billing || {}
        const _fullName = [_billing.name, _billing.lastname]
          .filter(Boolean)
          .join(' ')
          .trim()
        const _email = _billing.mail || undefined
        const _phone = _billing.phone || undefined
        const _country =
          _billing.country && _billing.country.code
            ? String(_billing.country.code).toUpperCase()
            : 'DE'
        if (process.env.NODE_ENV !== 'production') {
          console.log('[Stripe] Billing used for confirmPayment', {
            name: _fullName,
            email: _email,
            phone: _phone,
            country: _country,
          })
        }
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: `${window.location.origin}${window.location.pathname}`,
            payment_method_data: {
              billing_details: {
                // Wenn wir die Erfassung im Element deaktivieren (fields.billingDetails.* = "never"),
                // müssen wir die Daten hier mitgeben.
                name: _fullName,
                email: _email,
                phone: _phone,
                address: {
                  country: _country,
                },
              },
            },
          },
          redirect: 'if_required',
        })

        if (error) {
          console.error('Payment Error:', error)
          return
        }

        if (paymentIntent) {
          switch (paymentIntent.status) {
            case 'succeeded': {
              if (process.env.NODE_ENV !== 'production') {
                console.log('[Stripe] Success, waiting for webhook...')
              }
              // Check backend every second for 10 seconds to check if webhook has changed status in backend

              const isUpdated = await this.checkStatus(paymentIntent.id)

              if (isUpdated === 'confirmed') {
                await this.$router.push({
                  name: 'Confirmation',
                  params: {
                    language: this.$route.params.language,
                    campground: this.$route.params.campground,
                    bookingId: this.$route.params.bookingId,
                  },
                })
              } else if (isUpdated === 'failed') {
                this.addErrorMessage('pages.stripePayment.paymentFailed')
              } else if (isUpdated === 'pending') {
                console.log('Webhook has still not arrived!')
                await this.$router.push({
                  name: 'Confirmation',
                  params: {
                    language: this.$route.params.language,
                    campground: this.$route.params.campground,
                    bookingId: this.$route.params.bookingId,
                  },
                })
              }
              break
            }
            default:
              break
          }
        }
      } catch (error) {
        console.error('Unexpected Payment Error:', error)
        this.addErrorMessage('pages.stripePayment.paymentFailed')
      } finally {
        this.isLoading = false
        this.finishProcessingPayment()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.message {
  margin: 2rem auto;
  border-radius: 5px;
  max-width: 600px;

  &__error {
    border: 1px solid $primary-red;
    background-color: #f6b5b8ff;
  }

  &__info {
    border: 1px solid $yellow;
    background-color: rgba(248, 231, 28, 0.15);
  }

  &__text {
    position: relative;
    margin-bottom: 0;
    padding: 5px 15px;
    @include media-breakpoint-up(md) {
      padding: 10px;
    }

    &--info {
      color: $gray-text;
    }
  }

  &__title {
    color: #fff;
    @include font('gotham_mediumregular');
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 40px;
    padding: 0 25px;
    margin: 0;

    &--error {
      background: $primary-red;
    }

    &--info {
      color: black;
      background: $yellow;
    }
  }
}

.campground__navigation {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 20px 0;

  @include media-breakpoint-up(md) {
    flex-direction: row;
    margin: 20px 0;
  }
}

.loading {
  @include font('gotham_mediumregular');
  @include font-format(1rem, 1rem);
  display: flex;
  border-radius: 37.5px;
  padding: 15px 65px 15px 30px;
  justify-content: center;
}

#spinner {
  animation: rotation 0.7s infinite linear;
  border-radius: 100%;
  border: 3px solid $yellow;
  border-top-color: transparent;
  height: 28px;
  transition: 0.3s all ease;
  width: 28px;
  margin: 20px 0 0 30px;
}
</style>
